import { useMemo, useState } from "react";
import { FIELD_KEYS } from "@shared/constants/fields";
import { composeContactName } from "@shared/contact";
import { InterviewRowTable, type RowTableRow } from "./InterviewRowTable";
import { peopleStillLookedAfter, PERSON_MARK, type KnownPerson } from "./CarriedInTiles";
import { NoteCard, SegmentCell } from "./InterviewCells";
import { LocationBox, LocationBoxPanel } from "./LocationBox";
import { ConsequencePicker } from "./ConsequencePicker";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";
import { hasName, useChildRows, type ChildRow } from "./ChildrenScreen";
import { saveInBackground } from "./saveFailure";
import { ContactCard, LinkedPersonCell, cardSummary, type CardSlot } from "./PersonPicker";
import { boardCell, boardOptions, boardStep, boardTable } from "@shared/interview/board";

/**
 * s221 Lane 1 — THEIR DAY-TO-DAY (SPEC: `Main.dc.html` v289, the `daily` step).
 *
 * The tiles are the children under 18 and the dependants named on the screen before, carried in by
 * name (rule 42, mechanism 6). Each one picked gets a row saying what their day needs.
 *
 * 🔴 These answers live ON THE CHILD'S OWN ROW in children-dependents-overview (rule 121, D-221.10) —
 * the same rows the screen before wrote, through the same `useChildRows` (G7). So deselecting a person
 * here writes NOTHING: it only takes them out of this table. Their answers stay on their row, and the
 * plan still shows them. There is no row of this screen's own to hide.
 *
 * Doctor and school are the section's `text` fields, typed as plain names. The SPEC draws them as
 * contact cards; storing a contact value in a `text` field would render raw JSON on the plan, so the
 * flip to `contact` is a ruling for Lane 2, not a Lane 1 build decision.
 * "Allergies, medications and conditions" is ONE box on the SPEC board and lands in `conditions`.
 */

const DAY_FIELDS = [
  FIELD_KEYS.DOCTOR_NAME, FIELD_KEYS.CONDITIONS, FIELD_KEYS.SCHOOL_NAME, FIELD_KEYS.GRADE,
  FIELD_KEYS.ROUTINES, FIELD_KEYS.RAISED_IN_FAITH,
] as const;

// ── SPEC copy — read from the board (s222 BOARD-AS-SPEC-1, D-221.40): the `daily` step ──────────────
const DAILY = boardStep("daily");
const CELL = {
  doc: boardCell(DAILY, "doc"), health: boardCell(DAILY, "health"), school: boardCell(DAILY, "school"),
  grade: boardCell(DAILY, "grade"), day: boardCell(DAILY, "day"), faith: boardCell(DAILY, "faith"),
};
const GRADES = boardOptions("daily", CELL.grade);

// s221 Lane 1c gate (Reade): Who STAYS 140 here — this table's Who cell holds a full name, not the word
// "Child", and at 108 every name wraps. The board's `daily.cols`. (Children and dependants: 108.)
const TABLE = boardTable(DAILY);
/** A cell's accessible name: its column's header on the board. */
const headerOf = (k: string) => TABLE.columns.find((c) => c.key === k)?.label ?? "";

/** A writing box's words, from its board cell (`bigTitle` / `bigHint` / `bigPh`). */
const bigBox = (c: typeof CELL.health) => ({ title: c.bigTitle ?? "", hint: c.bigHint ?? "", placeholder: c.bigPh ?? "" });
const HEALTH = bigBox(CELL.health);
const DAY = bigBox(CELL.day);
/** The yes_no field stores "true" / "false" (the card-path convention); the board shows Yes / No (board-map.ts). */
const YES_NO = boardOptions("daily", CELL.faith);

const ADULT_NOTE = DAILY.noteFor?.d1 ?? "";

export function toKnownPerson(r: ChildRow): KnownPerson {
  return {
    rowId: r.rowId,
    name: composeContactName({ firstName: r.values[FIELD_KEYS.FIRST_NAME], surname: r.values[FIELD_KEYS.SURNAME] }),
    kind: r.kind,
    dob: r.values[FIELD_KEYS.DOB] ?? "",
  };
}

/** Has this row any day-to-day answer yet? Those rows start picked. Pure. */
export function hasDayAnswer(r: ChildRow): boolean {
  return DAY_FIELDS.some((k) => (r.values[k] ?? "").trim() !== "");
}

export function DayToDayScreen({ clientId, onAnswered, today }: { clientId: string; onAnswered: () => void; today?: string }) {
  return (
    <OnePanelProvider>
      <DayBody clientId={clientId} onAnswered={onAnswered} today={today ?? new Date().toISOString().slice(0, 10)} />
    </OnePanelProvider>
  );
}

function DayBody({ clientId, onAnswered, today }: { clientId: string; onAnswered: () => void; today: string }) {
  const data = useChildRows(clientId, onAnswered);
  const panels = usePanels();
  const visible = useMemo(() => data.rows.filter((r) => !r.hidden), [data.rows]);
  // 🔴 Fix 7: a row with no name is not a person — it is never carried in.
  const people = useMemo(() => peopleStillLookedAfter(visible.filter(hasName).map(toKnownPerson), today), [visible, today]);
  // Fix 6 (Reade): no tile picker — this screen only carries people who still need looking after, so the
  // question's answer was always yes. Each gets a row at once; × takes a row that does not apply off
  // THIS table (it writes nothing: the answers belong to the person's own row, rule 121).
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const everyone = people
    .map((p) => visible.find((r) => r.rowId === p.rowId))
    .filter((r): r is ChildRow => !!r);
  const picked = everyone.filter((r) => !removed.has(r.rowId));

  // s221 Lane 1d: a row taken off this table leaves "Name removed · Undo" in its place (shared row table).
  const rows: RowTableRow[] = everyone.map((r) => {
    if (removed.has(r.rowId)) {
      return { rowId: r.rowId, lead: toKnownPerson(r).name, cells: [],
        removed: { label: toKnownPerson(r).name, onUndo: () => setRemoved((prev) => { const n = new Set(prev); n.delete(r.rowId); return n; }) } };
    }
    const v = r.values;
    const tid = (what: string) => `interview-daily-${what}-${r.rowId}`;
    const healthKey = `${r.rowId}:health`;
    const dayKey = `${r.rowId}:day`;
    const noteKey = `${r.rowId}:note`;
    // s228 (INTERVIEW-SAVE-FAILURE): `saveNow` hands the save to a box that waits for it (the writing space's Done
    // stays open on a refusal); `write` starts it in the background for the cells that do not.
    const saveNow = (key: string) => (val: string) => data.writeCell(r.rowId, key, val);
    const write = (key: string) => (val: string) => saveInBackground(saveNow(key)(val));
    const save = (changes: Record<string, string>) => data.writeCells(r.rowId, changes);
    const docKey = `${r.rowId}:doctor`;
    const schoolKey = `${r.rowId}:school`;
    // Fix 1 — `doc`: the contact control; the doctor's name, phone and address land on the row's fields.
    // s222 (D-221.44): a doctor is a PERSON — the card takes its shape from the board's `doc` cell, which
    // sets no `org`, exactly as the school takes its own. Its name stays TEXT on the row so document
    // extraction keeps reading it (guardrail 2).
    const docSlots: CardSlot[] = [{ label: "The practice", org: CELL.doc.org, name: { kind: "text", key: FIELD_KEYS.DOCTOR_NAME }, phone: FIELD_KEYS.DOCTOR_PHONE, email: FIELD_KEYS.DOCTOR_EMAIL, address: FIELD_KEYS.DOCTOR_ADDRESS }];
    // Fix 1 — `school` is `org: true`: the board's company branch, then "A person to ask for".
    const schoolSlots: CardSlot[] = [
      { label: "The company", org: true, name: { kind: "text", key: FIELD_KEYS.SCHOOL_NAME }, phone: FIELD_KEYS.SCHOOL_PHONE, email: FIELD_KEYS.SCHOOL_EMAIL, address: FIELD_KEYS.SCHOOL_ADDRESS },
      { label: "A person to ask for", name: { kind: "contact", key: FIELD_KEYS.SCHOOL_CONTACT_PERSON }, phone: FIELD_KEYS.SCHOOL_CONTACT_PHONE, email: FIELD_KEYS.SCHOOL_CONTACT_EMAIL, address: FIELD_KEYS.SCHOOL_CONTACT_ADDRESS },
    ];
    return {
      rowId: r.rowId,
      lead: toKnownPerson(r).name || (r.kind === "child" ? "Your child" : "Your dependant"),
      leadMark: r.kind === "child" ? PERSON_MARK.child : PERSON_MARK.dependant,
      cells: [
        <LinkedPersonCell key="doc" value="" label={cardSummary(docSlots, v)} word={CELL.doc.word ?? ""}
          open={panels.isOpen(docKey)} onOpen={() => panels.toggle(docKey)} testId={tid("doctor")} />,
        <LocationBox key="health" value={v[FIELD_KEYS.CONDITIONS] ?? ""} placeholder={CELL.health.ph ?? ""} open={panels.isOpen(healthKey)}
          onOpen={() => panels.toggle(healthKey)} onCommit={saveNow(FIELD_KEYS.CONDITIONS)} testId={tid("health")} />,
        <LinkedPersonCell key="school" value="" org={CELL.school.org} label={cardSummary(schoolSlots.slice(0, 1), v)} word={CELL.school.word ?? ""}
          open={panels.isOpen(schoolKey)} onOpen={() => panels.toggle(schoolKey)} testId={tid("school")} />,
        <ConsequencePicker key="grade" value={v[FIELD_KEYS.GRADE] ?? ""} options={GRADES} placeholder={CELL.grade.ph} ariaLabel={headerOf("grade")} onCommit={write(FIELD_KEYS.GRADE)} testId={tid("grade")} />,
        <LocationBox key="day" value={v[FIELD_KEYS.ROUTINES] ?? ""} placeholder={CELL.day.ph ?? ""} open={panels.isOpen(dayKey)}
          onOpen={() => panels.toggle(dayKey)} onCommit={saveNow(FIELD_KEYS.ROUTINES)} testId={tid("day")} />,
        <SegmentCell key="faith" value={v[FIELD_KEYS.RAISED_IN_FAITH] ?? ""} ariaLabel={headerOf("faith")} options={YES_NO}
          onCommit={write(FIELD_KEYS.RAISED_IN_FAITH)} testId={tid("faith")} />,
      ],
      note: {
        value: v[FIELD_KEYS.CARE_NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.CARE_NOTES] ?? "") ? saveNow(FIELD_KEYS.CARE_NOTES)(val) : undefined),
      },
      panels: (
        <>
          {panels.isOpen(docKey) && (
            <ContactCard interviewLook title={CELL.doc.title ?? ""} slots={docSlots} values={v} fieldIdByKey={data.fieldIdByKey} clientId={clientId}
              undo={CELL.doc.undo} onSave={save} onClose={() => panels.close(docKey)} testId={tid("doctor")} />
          )}
          {panels.isOpen(schoolKey) && (
            <ContactCard interviewLook title={CELL.school.title ?? ""} slots={schoolSlots} values={v} fieldIdByKey={data.fieldIdByKey} clientId={clientId}
              undo={CELL.school.undo} moreLabel="+ Add a person to ask for" onSave={save} onClose={() => panels.close(schoolKey)} testId={tid("school")} />
          )}
          {panels.isOpen(healthKey) && (
            <LocationBoxPanel {...HEALTH} value={v[FIELD_KEYS.CONDITIONS] ?? ""} onCommit={saveNow(FIELD_KEYS.CONDITIONS)} onClose={() => panels.close(healthKey)} testId={tid("health")} />
          )}
          {panels.isOpen(dayKey) && (
            <LocationBoxPanel {...DAY} value={v[FIELD_KEYS.ROUTINES] ?? ""} onCommit={saveNow(FIELD_KEYS.ROUTINES)} onClose={() => panels.close(dayKey)} testId={tid("day")} />
          )}
        </>
      ),
    };
  });

  return (
    <div data-testid="interview-daily">

      <InterviewRowTable rowMarks strongRowLine {...TABLE} rows={rows}
        // × takes the person out of this table only — their answers stay on their own row (rule 121).
        onRemove={(id) => setRemoved((prev) => new Set(prev).add(id))} testIdPrefix="interview-daily-rows"
        // s226 (board v345): daily's name cell is the first to use the shared cut-text control (Reade's addendum).
        // s233 (board 1 v443): "a name on one line beside its mark" — one line, cut names open in full.
        cutLead leadOneLine />
      {/* The board's note card, after the table (Main.dc.html `noteFor.d1`). */}
      {picked.some((r) => r.kind === "dependant") && (
        <div className="mt-6">
          <NoteCard {...PERSON_MARK.dependant} testId="interview-daily-note-dependant">{ADULT_NOTE}</NoteCard>
        </div>
      )}
    </div>
  );
}
